import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '@/components/Ad/Promotions/Promotion.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { formatNumber } from '@/utils/helpers';
import { PromotionProps } from '@/components/Ad/Promotions/Promotions.types';
import { AdKind } from '@/api/types/common.types';
import { useAdDetails } from '@/hooks/useAdDetails';
import {
  hasSales,
  hasOneSale,
} from '@/components/Ad/Promotions/Promotions.helpers';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import {
  getPromotionId,
  getTestId,
} from '@/components/Ad/Promotions/Promotion.helpers';
import { useServerDataContext } from '@/contexts';

export const Promotion: React.FC<PromotionProps> = ({
  promotion,
  openDrawer,
  setType,
}) => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const isAuthor = useIsAuthor();

  const isActive =
    promotion.type !== AdKind.Bumped && adData?.kinds.includes(promotion.type);

  function handleDrawer(e: React.MouseEvent) {
    e.preventDefault();
    openDrawer();
    setType(promotion.type);
  }

  if (!adData) return;

  const services = {
    [AdKind.Bumped]: adData.bumpServices,
    [AdKind.Vipped]: adData.vipServices,
    [AdKind.Premium]: adData.featuredServices,
  };

  const firstService = services[promotion.type][0];

  function getPrice(hasDecimals = false) {
    return t('pages.ad.price_from', {
      price: formatNumber(promotion.minPrice, hasDecimals ? 2 : 0),
    });
  }

  const prevPrice = t('pages.ad.price_from', {
    price: formatNumber(services[promotion.type][0].previousPrice, 2),
  });

  if (!isAuthor) {
    return (
      <S.SmallBox
        id={getPromotionId(promotion.type)}
        href="#"
        $isActive={isActive}
        onClick={handleDrawer}
        data-testid={getTestId(promotion.type, isAuthor)}
      >
        <S.SmallTitle>{promotion.title}</S.SmallTitle>
        <S.SmallPrice>{getPrice()}</S.SmallPrice>
        <S.SmallIcon
          $filename={promotion.icon}
          $iconWidth={15}
          $iconHeight={13}
        />
      </S.SmallBox>
    );
  }

  return (
    <S.Box href="#" $isActive={isActive} onClick={handleDrawer}>
      <S.Texts $isBoxLabelPresented={!!promotion.boxLabel}>
        <S.TitleBox>
          <S.Title>{promotion.title}</S.Title>
          <S.Icons>
            <IconBox
              $filename={promotion.icon}
              $iconWidth={15}
              $iconHeight={13}
            />
          </S.Icons>
        </S.TitleBox>
        <S.Description>{promotion.description}</S.Description>
      </S.Texts>

      {!isActive && (
        <S.Actions>
          {hasOneSale(firstService) ? (
            <S.PrevPrice>{prevPrice}</S.PrevPrice>
          ) : (
            hasSales(services[promotion.type]) && (
              <S.Label>{t('pages.ad.favorable_prices')}</S.Label>
            )
          )}
          <S.Action
            appearance="secondary"
            size="s"
            data-testid={getTestId(promotion.type, isAuthor)}
          >
            {getPrice(true)}
          </S.Action>
        </S.Actions>
      )}

      {promotion.boxLabel && (
        <S.BoxLabel
          $isVipBg={promotion.type === AdKind.Vipped}
          $isPremiumBg={promotion.type === AdKind.Premium}
        >{`+ ${promotion.boxLabel}`}</S.BoxLabel>
      )}
    </S.Box>
  );
};
